import { formatMoney } from '@theme/utilities';
import { QuantityBreaksUpdatedEvent, ThemeEvents } from '@theme/events';

class QuantityBreaks extends HTMLElement {
  constructor() {
    super();

    this.default_quantity_selected = null;
    this.quantity_break_items = null;
  }

  connectedCallback() {
    this.default_quantity_selected = this.dataset.quantitySelectedByDefault || 1;
    this.quantity_break_items = this.querySelectorAll('[data-ref="quantity-break-item"]');

    this.updateDefaultQuantitySelected();

    this.quantity_break_items.forEach((quantity_break) => {
      quantity_break.addEventListener('click', () => {
        // Ne pas sélectionner si l'item est déjà sélectionné ou désactivé
        if (quantity_break.classList.contains('quantity-break--selected')) return;
        if (quantity_break.classList.contains('quantity-break--disabled')) return;
        this.selectQuantity(quantity_break);
      });
    });

    document.addEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated.bind(this));
  }

  updateDefaultQuantitySelected() {
    this.resetSelectedQuantity();
    this.quantity_break_items[this.default_quantity_selected - 1].classList.add('quantity-break--selected');
  }

  selectQuantity(quantity_break) {
    this.resetSelectedQuantity();
    this.resetCrossSellCheckboxes();
    quantity_break.classList.add('quantity-break--selected');

    this.dispatchEvent(new QuantityBreaksUpdatedEvent());
  }

  resetCrossSellCheckboxes() {
    this.querySelectorAll('[data-ref="quantity-break-cross-sell-checkbox"]').forEach((checkbox) => {
      checkbox.checked = false;
    });
  }

  resetSelectedQuantity() {
    this.quantity_break_items.forEach((quantity_break) => {
      quantity_break.classList.remove('quantity-break--selected');
    });
  }

  getSelectedQuantity() {
    return this.querySelector('.quantity-break--selected');
  }

  getPrices() {
    return this.getSelectedQuantity().getPrices();
  }

  getCrossSellPrices() {
    return this.getSelectedQuantity().getCrossSellPrices();
  }

  getDiscount() {
    return this.getSelectedQuantity().getDiscount();
  }

  getSelectedItems() {
    let items = [];

    if (this.isVariantSelectorActivated()) {
      this.getSelectedQuantity()
        .querySelectorAll('[data-ref="quantity-break-variant-picker-row"]')
        .forEach((variant_row) => {
          items.push({
            id: variant_row.dataset.variantId,
            quantity: 1,
          });
        });
    } else {
      items.push({
        id: this.getSelectedQuantity().dataset.variantId,
        quantity: parseInt(this.getSelectedQuantity().dataset.quantity),
      });
    }

    // Ajouter les cross-sells cochés
    const crossSellItems = this.getSelectedQuantity().getCrossSellItems();
    items = [...items, ...crossSellItems];

    return items;
  }

  /**
   * Vérifie si le selector de variantes dans le quantity break est activé
   */
  isVariantSelectorActivated() {
    return this.dataset.showVariantPicker == 'true';
  }

  onVariantUpdated(event) {
    // Quand le picker par unité est actif, le sélecteur global sert de valeur par défaut pour toutes
    // les unités de tous les packs plutôt que de remplacer le DOM (voir QuantityBreakItem.syncRowsToVariant)
    if (this.isVariantSelectorActivated()) {
      const variant = event?.detail?.resource;
      if (!variant) return;

      this.querySelectorAll('[data-ref="quantity-break-item"]').forEach((item) => item.syncRowsToVariant(variant));
      this.dispatchEvent(new QuantityBreaksUpdatedEvent());
      return;
    }

    // On récupère le DOM actuel du composant
    const currentQuantityBreaks = this;

    // Le event.detail est null si l'évenement de mise à jour est déclenché par les cross-sells à activer et non par les variantes, dans ce cas là on utilise le DOM actuel
    let newQuantityBreaks = currentQuantityBreaks;
    if (event && event.detail != null) {
      newQuantityBreaks = event.detail.data.html.querySelector('[data-ref="quantity-breaks"]');
    }

    if (!newQuantityBreaks || !currentQuantityBreaks) return;

    // On remplace le DOM actuel par le nouveau DOM
    if (currentQuantityBreaks !== newQuantityBreaks) {
      currentQuantityBreaks.querySelectorAll('[data-ref="quantity-break-item"]').forEach((quantity_break, index) => {
        const newQuantityBreak = newQuantityBreaks.querySelectorAll('[data-ref="quantity-break-item"]')[index];
        quantity_break.render(newQuantityBreak);
      });

      // Si l'item actuellement sélectionné est maintenant désactivé, on sélectionne le premier item disponible
      this.#handleDisabledSelectedItem();

      this.dispatchEvent(new QuantityBreaksUpdatedEvent());
    }
  }

  #handleDisabledSelectedItem() {
    const selectedItem = this.getSelectedQuantity();
    if (!selectedItem || !selectedItem.isDisabled()) return;

    // Trouver le premier item non désactivé
    const firstAvailableItem = Array.from(this.quantity_break_items).find((item) => !item.classList.contains('quantity-break--disabled'));

    if (firstAvailableItem) {
      this.resetSelectedQuantity();
      this.resetCrossSellCheckboxes();
      firstAvailableItem.classList.add('quantity-break--selected');
    }
  }
}

customElements.define('quantity-breaks', QuantityBreaks);

class QuantityBreakItem extends HTMLElement {
  constructor() {
    super();

    this.variant_rows = null;
    this.variantsData = [];
    this.price = null;
    this.unit_price = null;
    this.cross_sells = null;
  }

  connectedCallback() {
    this.variant_rows = this.querySelectorAll('[data-ref="quantity-break-variant-picker-row"]');
    this.price = this.querySelector('[data-ref="quantity-break-price"]');
    this.unit_price = this.querySelector('[data-ref="quantity-break-unit-price"]');
    this.cross_sells = this.querySelectorAll('[data-ref="quantity-break-cross-sell"]');

    const variantsScript = this.closest('quantity-breaks')?.querySelector('[data-ref="quantity-breaks-variants"]');
    this.variantsData = variantsScript ? JSON.parse(variantsScript.textContent) : [];

    this.variant_rows.forEach((row) => {
      // Résout la variante initiale (calcule aussi la disponibilité des combinaisons dès le chargement)
      this.#resolveRowVariant(row);

      row.querySelectorAll('[data-ref="quantity-break-variant-picker"]').forEach((select) => {
        select.addEventListener('change', () => {
          this.#resolveRowVariant(row, select);
          this.#updateTotalPrice();

          this.dispatchEvent(new QuantityBreaksUpdatedEvent());
        });
      });
    });

    // Clic sur la ligne du cross-sell pour toggler le checkbox
    this.cross_sells.forEach((cross_sell) => {
      const checkbox = cross_sell.querySelector('[data-ref="quantity-break-cross-sell-checkbox"]');
      const checkboxLabel = checkbox?.closest('.checkbox');

      cross_sell.addEventListener('click', (e) => {
        e.stopPropagation();

        // Toggle le checkbox si le clic n'est pas sur le label du checkbox (qui gère déjà le toggle)
        if (checkbox && !checkboxLabel?.contains(e.target)) {
          checkbox.checked = !checkbox.checked;
          this.dispatchEvent(new QuantityBreaksUpdatedEvent());
        }
      });

      checkbox?.addEventListener('change', () => {
        this.dispatchEvent(new QuantityBreaksUpdatedEvent());
      });
    });
  }

  /**
   * Retourne les selects (un par option, ex: Couleur / Taille) d'une ligne, triés par position.
   */
  #getRowSelects(row) {
    return Array.from(row.querySelectorAll('[data-ref="quantity-break-variant-picker"]')).sort(
      (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition),
    );
  }

  /**
   * Trouve la variante correspondant à une combinaison de valeurs d'options (dans l'ordre option1/2/3).
   */
  #findVariant(optionValues) {
    return this.variantsData.find((variant) => optionValues.every((value, index) => variant[`option${index + 1}`] === value));
  }

  /**
   * Désactive les valeurs d'option qui ne mènent à aucune variante disponible, étant donné les autres
   * sélections en cours dans la ligne (cascading standard, empêche de choisir une combinaison inexistante).
   */
  #updateOptionsAvailability(selects) {
    selects.forEach((select, selectIndex) => {
      Array.from(select.options).forEach((option) => {
        const testValues = selects.map((s, i) => (i === selectIndex ? option.value : s.value));
        const variant = this.#findVariant(testValues);
        option.disabled = !variant || !variant.available;
      });
    });
  }

  /**
   * Résout la variante d'une ligne à partir de ses selects, met à jour son prix/image, et recale les
   * autres selects si le changement rend leur valeur actuelle indisponible.
   */
  #resolveRowVariant(row, changedSelect = null) {
    const selects = this.#getRowSelects(row);
    if (selects.length === 0) return;

    this.#updateOptionsAvailability(selects);

    selects.forEach((select) => {
      if (select === changedSelect) return;
      const currentOption = select.options[select.selectedIndex];
      if (currentOption?.disabled) {
        const firstAvailable = Array.from(select.options).find((option) => !option.disabled);
        if (firstAvailable) select.value = firstAvailable.value;
      }
    });

    this.#updateOptionsAvailability(selects);

    const matchedVariant = this.#findVariant(selects.map((select) => select.value));
    if (!matchedVariant) return;

    row.dataset.variantId = matchedVariant.id;
    row.dataset.price = matchedVariant.price;
    row.dataset.compareAtPrice =
      matchedVariant.compare_at_price > matchedVariant.price ? matchedVariant.compare_at_price : matchedVariant.price;

    this.#updateThumbnailForRow(row, matchedVariant);
  }

  /**
   * Applique une variante (choisie via le sélecteur global du haut de fiche) à toutes les lignes de cet
   * item : sert de valeur par défaut pour tout le pack, que le client peut ensuite personnaliser ligne par ligne.
   */
  syncRowsToVariant(variant) {
    if (!this.variant_rows || this.variant_rows.length === 0) return;

    this.variant_rows.forEach((row) => {
      this.#getRowSelects(row).forEach((select, index) => {
        const value = variant[`option${index + 1}`];
        if (value != null) select.value = value;
      });
      this.#resolveRowVariant(row);
    });

    this.#updateTotalPrice();
  }

  #updateThumbnailForRow(row, variant) {
    const thumbnail_container = row.querySelector('[data-ref="quantity-break-variant-thumbnail"]');
    if (!thumbnail_container) return;

    const variantImageUrl = variant.featured_image?.src;
    const variantImageAlt = variant.featured_image?.alt || '';

    if (variantImageUrl) {
      thumbnail_container.classList.remove('quantity-break__variant-thumbnail--hidden');
      thumbnail_container.innerHTML = `<img src="${variantImageUrl}" alt="${variantImageAlt}" width="40" height="40" loading="lazy">`;
    } else {
      thumbnail_container.classList.add('quantity-break__variant-thumbnail--hidden');
      thumbnail_container.innerHTML = '';
    }
  }

  #updateTotalPrice() {
    let totalPrice = 0;
    let totalCompareAtPrice = 0;

    this.variant_rows.forEach((row) => {
      totalPrice += parseInt(row.dataset.price);
      totalCompareAtPrice += parseInt(row.dataset.compareAtPrice);
    });

    // On calcule le montant de la réduction, en fonction du type de réduction (valeur ou pourcentage)
    const discountType = this.dataset.discountType;
    const discountValue = this.dataset.discountValue;
    const discountPercentage = this.dataset.discountPercentage;

    let totalDiscountValue = discountValue;
    if (discountType === 'percentage') {
      totalDiscountValue = (totalPrice * discountPercentage) / 100;
    }

    // On soustrait le montant de la réduction au prix total (minimum 0€)
    totalPrice = Math.max(0, totalPrice - totalDiscountValue);

    // On met à jour le prix total du pack (utilisé par le bouton d'ajout au panier), indépendamment de l'affichage
    this.dataset.totalPrice = totalPrice;
    this.dataset.totalCompareAtPrice = totalCompareAtPrice > totalPrice ? totalCompareAtPrice : totalPrice;

    // Le prix total et le prix par unité sont affichés indépendamment, chacun seulement si son bloc est présent
    if (this.price) {
      this.#setPriceDisplay(this.price, totalPrice, totalCompareAtPrice);
    }

    if (this.unit_price) {
      const quantity = parseInt(this.dataset.quantity) || 1;
      this.#setPriceDisplay(this.unit_price, Math.round(totalPrice / quantity), Math.round(totalCompareAtPrice / quantity));
    }
  }

  #setPriceDisplay(container, price, comparePrice) {
    container.querySelector('[data-ref="price"]').innerHTML = formatMoney(price);
    container.querySelector('[data-ref="compare-at-price"]').innerHTML = comparePrice > price ? formatMoney(comparePrice) : '';
  }

  render(newQuantityBreak) {
    // Met à jour les datasets de prix
    this.dataset.totalPrice = newQuantityBreak.dataset.totalPrice;
    this.dataset.totalCompareAtPrice = newQuantityBreak.dataset.totalCompareAtPrice;
    this.dataset.discountTotalValue = newQuantityBreak.dataset.discountTotalValue;
    this.dataset.variantId = newQuantityBreak.dataset.variantId;

    // Met à jour les prix (total et/ou par unité, selon les blocs présents)
    if (this.price) {
      const newPrice = newQuantityBreak.querySelector('[data-ref="quantity-break-price"]');
      this.price.querySelector('[data-ref="price"]').innerHTML = newPrice.querySelector('[data-ref="price"]').innerHTML;
      this.price.querySelector('[data-ref="compare-at-price"]').innerHTML = newPrice.querySelector('[data-ref="compare-at-price"]').innerHTML;
    }

    if (this.unit_price) {
      const newUnitPrice = newQuantityBreak.querySelector('[data-ref="quantity-break-unit-price"]');
      this.unit_price.querySelector('[data-ref="price"]').innerHTML = newUnitPrice.querySelector('[data-ref="price"]').innerHTML;
      this.unit_price.querySelector('[data-ref="compare-at-price"]').innerHTML = newUnitPrice.querySelector('[data-ref="compare-at-price"]').innerHTML;
    }

    // Met à jour l'image
    if (this.querySelector('[data-ref="quantity-break-image"]')) {
      this.querySelector('[data-ref="quantity-break-image"]').src = newQuantityBreak.querySelector('[data-ref="quantity-break-image"]').src;
    }

    // Met à jour l'état désactivé en fonction de la disponibilité de la nouvelle variante
    const isNewQuantityBreakDisabled = newQuantityBreak.classList.contains('quantity-break--disabled');
    this.classList.toggle('quantity-break--disabled', isNewQuantityBreakDisabled);
  }

  isDisabled() {
    return this.classList.contains('quantity-break--disabled');
  }

  getPrices() {
    const price = parseInt(this.dataset.totalPrice);
    const compareAtPrice = parseInt(this.dataset.totalCompareAtPrice);

    return {
      price: price,
      compareAtPrice: compareAtPrice,
    };
  }

  getCrossSellPrices() {
    return this.#getCheckedCrossSellPrices();
  }

  getCrossSellItems() {
    const items = [];

    // Récupérer dynamiquement les cross-sells pour éviter les problèmes de références
    const crossSells = this.querySelectorAll('[data-ref="quantity-break-cross-sell"]');
    crossSells.forEach((cross_sell) => {
      const checkbox = cross_sell.querySelector('[data-ref="quantity-break-cross-sell-checkbox"]');
      if (checkbox?.checked) {
        items.push({
          id: cross_sell.dataset.variantId,
          quantity: 1,
        });
      }
    });

    return items;
  }

  #getCheckedCrossSellPrices() {
    let price = 0;
    let compareAtPrice = 0;

    // Récupérer dynamiquement les cross-sells pour éviter les problèmes de références
    const crossSells = this.querySelectorAll('[data-ref="quantity-break-cross-sell"]');
    crossSells.forEach((cross_sell) => {
      const checkbox = cross_sell.querySelector('[data-ref="quantity-break-cross-sell-checkbox"]');
      if (checkbox?.checked) {
        const itemPrice = parseInt(cross_sell.dataset.price) || 0;
        const itemCompareAtPrice = parseInt(cross_sell.dataset.compareAtPrice) || itemPrice;
        price += itemPrice;
        compareAtPrice += itemCompareAtPrice;
      }
    });

    return { price, compareAtPrice };
  }

  getDiscount() {
    return {
      type: this.dataset.discountType || 'percentage',
      value: parseInt(this.dataset.discountValue) || 0,
      percentage: parseInt(this.dataset.discountPercentage) || 0,
    };
  }
}
customElements.define('quantity-break-item', QuantityBreakItem);
